import { z } from 'zod';
import { ChangesSchema, RecipeInfoSchema } from './campaigns';

export const HappyHourSchema = z.object({
	enabled: z.boolean().optional(),
	range: z.tuple([z.number(), z.number()]).optional(),
});

export const StaticWindowSchema = z.object({
	from: z.any(),
	to: z.any(),
});

export const RollingWindowSchema = z.object({
	from: z.number().optional(),
	to: z.number().optional(),
});

export const TraitTargetSchema = z.object({
	value: z.any().optional(),
	rolling: RollingWindowSchema.optional(),
	static: StaticWindowSchema.optional(),
	comparison: z.string().optional(),
	type: z.string().optional(),
	stringTarget: z.string().optional(),
});

export const AudienceChangeSetSchema: z.ZodType<any> = z.lazy(() =>
	z.object({
		approved: z.boolean().optional(),
		uid: z.string().optional(),
		changes: z.record(ChangesSchema.optional()).optional(),
		diffRetailer: AudienceSchema.optional(),
		diffBrand: AudienceSchema.optional(),
		updated: z.number().optional(),
	}),
);

export const DataFeedSchema = z.object({
	name: z.string().optional(),
	id: z.string().optional(),
});

export const GeoRestrictionSchema = z.object({
	schools: z.number().optional(),
	postSecondarySchool: z.number().optional(),
	childcareCenter: z.number().optional(),
	playground: z.number().optional(),
	substanceTreatmentCenter: z.number().optional(),
	publicProperty: z.number().optional(),
	eSports: z.number().optional(),
	sports: z.number().optional(),
	transport: z.number().optional(),
	outsideGeoCollected: z.boolean().optional(),
});

export const AgeRestrictionSchema = z.object({
	ageRestriction: z.number().optional(),
	firstPartyAgeGateRequired: z.boolean().optional(),
	thirdPartyAgeGatePercentage: z.number().optional(),
});

export const ContentRestrictionSchema = z.object({
	depictingCartoon: z.boolean().optional(),
	depictingPerson: z.boolean().optional(),
	depictingEmotionalChangeInLife: z.boolean().optional(),
	falseOrMisleading: z.boolean().optional(),
	creativeApprovalRequiredByState: z.boolean().optional(),
	creativeApprovalDays: z.number().optional(),
});

export const ChannelRestrictionSchema = z.object({
	popup: z.boolean().optional(),
	banner: z.boolean().optional(),
	text: z.boolean().optional(),
	directoryListing: z.boolean().optional(),
	radio: z.boolean().optional(),
	tv: z.boolean().optional(),
	email: z.boolean().optional(),
	snailMail: z.boolean().optional(),
	webPublisher: z.boolean().optional(),
	printPublisher: z.boolean().optional(),
	billboard: z.boolean().optional(),
	coupon: z.boolean().optional(),
	giveaway: z.boolean().optional(),
	competitions: z.boolean().optional(),
});

export const ProductRestrictionSchema = z.object({
	concentrates: z.boolean().optional(),
	vape: z.boolean().optional(),
	vapeFlavored: z.boolean().optional(),
	vapeCBD: z.boolean().optional(),
	ediblesCBD: z.boolean().optional(),
	edibles: z.boolean().optional(),
	topicals: z.boolean().optional(),
	topicalsCBD: z.boolean().optional(),
	bev: z.boolean().optional(),
	bevCBD: z.boolean().optional(),
	flower: z.boolean().optional(),
});

export const LawRestrictionsSchema = z.object({
	states: z.array(z.string()).optional(),
	contentRestrictions: ContentRestrictionSchema,
	channelRestrictions: ChannelRestrictionSchema,
	productRestrictions: ProductRestrictionSchema,
});

export const StateLawSchema = z.object({
	locationName: z.string().nullable(),
	code: z.string().nullable(),
	geoRestrictions: GeoRestrictionSchema.optional(),
	ageRestrictions: AgeRestrictionSchema.optional(),
	contentRestrictions: ContentRestrictionSchema.optional(),
	channelRestrictions: ChannelRestrictionSchema.optional(),
	productRestrictions: ProductRestrictionSchema.optional(),
	specialStatements: z.array(z.string()),
	coopAdvertising: z.boolean(),
	easyOptOut: z.boolean(),
	restrictionsLastUpdated: z.number(),
	restrictionsSourcedFrom: z.array(z.string()),
});

export const AudienceSchema = z.object({
	happyHours: z.array(HappyHourSchema.optional()).optional(),
	overrideLaws: StateLawSchema.optional(),
	dataFeeds: z.record(DataFeedSchema.optional()).optional(),
	type: z.string(),
	name: z.string(),
	dResp: z.string(),
	id: z.string(),
	gid: z.string(),
	userID: z.string(),
	skuMulti: z.string(),
	catMulti: z.string(),
	brandMulti: z.string(),
	pmtMethodMulti: z.string(),
	pmtMethodMultipliers: z.array(z.string()),
	avatar: z.string(),
	storeIDsMulti: z.array(z.number()).optional(),
	destinations: z.array(z.number()).optional(),
	salesReports: z.array(z.string()),
	storesReports: z.array(z.string()),
	sources: z.array(z.number()).optional(),
	prpts: z.array(z.string()),
	rrpts: z.array(z.string()),
	erpts: z.array(z.string()),
	traits: z.array(TraitTargetSchema).optional(),
	laws: LawRestrictionsSchema.optional(),
	created: z.number(),
	updated: z.number(),
	salesStartRep: z.number(),
	storesStartRep: z.number(),
	storesEndRep: z.number(),
	endRep: z.number(),
	size: z.number(),
	optInEmailSize: z.number(),
	optInPhoneSize: z.number(),
	optInPushSize: z.number(),
	optInVoiceSize: z.number(),
	optInLoyaltySize: z.number(),
	CLTV: z.number(),
	multiplier: z.number(),
	boost: z.number(),
	zero: z.boolean(),
	multiStart: z.number(),
	lastPointsRun: z.number().optional(),
	lastSave: z.number().optional(),
	startRep: z.number(),
	salesEndRep: z.number(),
	multiEnd: z.number(),
	isActive: z.boolean().optional(),
	anonUsers: z.boolean().optional(),
	anonContactLocations: z.boolean().optional(),
	archived: z.boolean().optional(),
	lockedReason: z.string().optional(),
	orComp: z.boolean(),
	userWhitelist: z.array(z.string()).optional(),
	default: z.boolean().optional(),
	lastT: z.number(),
	wasGeneratedAI: z.boolean().optional(),
	creationPrompt: z.string().optional(),
	hasChangedSincePrompt: z.boolean().optional(),
	generatedDescription: z.string().optional(),
	description: z.string().optional(),
	recipe: RecipeInfoSchema.optional(),
	recipes: z.record(z.boolean()).optional(),
	isLocked: z.boolean().optional(),
	changeSet: AudienceChangeSetSchema.optional(),
	coMarketing: z.any().optional(),
});
